import React, { useMemo } from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { useSvgId } from '../../../utils/hooks/useSvgId';
import { EventReward } from './eventMock';
import { EVENT_FONT, eventColors, eventGlow, TILE_CLAIMABLE_FILL, TILE_FILL } from './eventTheme';

// Figma component "Reward Tile / Aligned" (3161:13280): the icon and the count sit in the same
// place in every state - a fixed 36x40 icon slot over a 14/20 count - so the counts line up
// across the row. Only the surface changes when a tile is claimable.
const ICON_SLOT_WIDTH = 36;
const ICON_SLOT_HEIGHT = 40;

type Props = {
	reward: EventReward;
	width: number;
	height: number;
	claimable: boolean;
	/** Milestone not reached yet - the tile keeps its art but loses its punch. */
	dimmed: boolean;
	scale: number;
};

export const RewardTile = ({ reward, width, height, claimable, dimmed, scale }: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const fillId = useSvgId('eventRewardTile');

	return (
		<View
			style={[
				styles.tile,
				{ width, height },
				claimable ? styles.tileClaimable : styles.tileDefault,
				dimmed && styles.tileDimmed,
			]}
		>
			<View pointerEvents='none' style={[styles.fill, { borderRadius: 5 * scale }]}>
				<Svg width='100%' height='100%'>
					<Defs>
						<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
							{(claimable ? TILE_CLAIMABLE_FILL : TILE_FILL).map(([offset, color]) => (
								<Stop key={offset} offset={offset} stopColor={color} />
							))}
						</LinearGradient>
					</Defs>
					<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${fillId})`} />
				</Svg>
			</View>

			<Image source={reward.icon} style={styles.icon} resizeMode='contain' />
			<Text numberOfLines={1} style={styles.amount}>
				{reward.amountLabel}
			</Text>
		</View>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		tile: {
			borderRadius: 5 * scale,
			alignItems: 'center',
			justifyContent: 'center',
			paddingVertical: 4 * scale,
			overflow: 'hidden',
		},
		fill: {
			...StyleSheet.absoluteFillObject,
			overflow: 'hidden',
		},
		tileDefault: {
			borderWidth: Math.max(1, 1 * scale),
			borderColor: eventColors.tileBorder,
		},
		tileClaimable: {
			borderWidth: Math.max(1, 1 * scale),
			borderColor: eventColors.panelBorderBright,
			...eventGlow.bloom,
		},
		tileDimmed: {
			opacity: 0.55,
		},
		icon: {
			width: ICON_SLOT_WIDTH * scale,
			height: ICON_SLOT_HEIGHT * scale,
		},
		// Figma: Sofia Sans SemiBold 14 / 20, text/accent.
		amount: {
			color: eventColors.accent,
			fontFamily: EVENT_FONT,
			fontSize: 14 * scale,
			fontWeight: '600',
			lineHeight: 20 * scale,
		},
	});

export default RewardTile;
